import { useContext } from 'react';
import TableContext from './TableContext';
import classNames from 'classnames';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * `TableCell` dynamically renders a `<th>` or `<td>` element based on the
 * parent component or user specified `component` prop. By default `TableCell`
 * will automatically render a `<th>` element if the parent component is
 * `TableHead`, otherwise it will render a `<td>` element.
 */
export const TableCell = _ref => {
  let {
    align,
    children,
    className,
    component,
    headers,
    id,
    scope,
    stackedTitle,
    stackedClassName,
    _isTableHeadChild,
    ...tableCellProps
  } = _ref;
  const {
    stackable,
    warningDisabled
  } = useContext(TableContext);
  let Component;
  if (component) {
    Component = component;
  } else {
    Component = _isTableHeadChild ? 'th' : 'td';
  }
  if (process.env.NODE_ENV !== 'production' && stackable && !warningDisabled) {
    // Provide warning message for `id` prop for cells with parent component of `TableHead`
    if (_isTableHeadChild) {
      if (!id && children) {
        console.warn('The id prop in `TableCell` is required for stackable tables. This prop is needed to assign an id to a heading in the responsive stacked view.');
      }
    } else if (Component === 'td') {
      // Provide warning message for stacktable `headers` and `stackedTitle` props
      if (!headers) {
        console.warn('The headers prop in `TableCell` is required for stackable tables. This prop is needed to associate the headings with data cells in the responsive stacked view.');
      }
      if (!stackedTitle) {
        console.warn('The stackedTitle prop in `TableCell` is required for stackable tables. This prop is displayed for the data cell in the responsive stacked view.');
      }
    }
  }
  let role = 'cell';
  if (_isTableHeadChild) {
    role = 'columnheader';
  } else if (component === 'th') {
    role = 'rowheader';
  }
  let defaultScope = scope;
  if (!defaultScope && _isTableHeadChild) {
    defaultScope = 'col';
  }
  const alignClassName = align ? `ds-u-text-align--${align}` : null;
  const classes = classNames(alignClassName, className);

  // The data attributes `data-title` is access by CSS to generates row header content for stacked table
  return /*#__PURE__*/_jsx(Component, {
    className: classes,
    role: role,
    scope: defaultScope,
    headers: headers,
    id: id,
    "data-title": stackedTitle,
    ...tableCellProps,
    children: children
  });
};
TableCell.defaultProps = {
  align: 'left'
};
export default TableCell;